/**
 * A brand-new paragraph's markup from plain text.
 *
 * The edit model asks for a new paragraph with `insert_paragraph_after`, as
 * text. `OoxmlRange.insertParagraph` needs a `w:p` to copy in and mark as an
 * insertion, so this builds one: the paragraph properties of the paragraph it
 * goes after, so the new paragraph keeps the same style, numbering and
 * indentation, and one run holding the text.
 */
import { FRAGMENT_NAMESPACE_DECLARATIONS, WORDML_2010_NS, WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "./ooxml-names";
import type { ParagraphListContinuation } from "../wire/redline-payload";
import { XmlHost } from "../xml-host";

export interface PlainParagraphFromTextRequest {
    text: string;
    /** The `w:p` the new paragraph follows; its `w:pPr` is copied so the new paragraph looks like its neighbour. */
    templateParagraphXml: string;
}

export interface InheritParagraphPropertiesRequest {
    /** The `w:p` about to be inserted, as the redline source built it. */
    paragraphXml: string;
    /** The live `w:p` it will follow; its `w:pPr` is copied when `paragraphXml` has none. "" when the range has no paragraph. */
    templateParagraphXml: string;
    /** "continue" copies the template's properties onto a bare paragraph; "none" leaves it bare, a plain paragraph. */
    list: ParagraphListContinuation;
}

export class ParagraphXml {
    /**
     * `paragraphXml` with the template's paragraph properties when it carries
     * none of its own: a text card's insertion is built from text alone, so
     * without this a paragraph added inside a numbered list would lose the
     * numbering, style and indent of the paragraph it follows.
     *
     * CASES
     *   - the model asked for a plain paragraph (`list` "none")           -> returned unchanged: a closing
     *                                                                        sentence after the last bullet
     *                                                                        is not a bullet
     *   - the paragraph already has a `w:pPr` (a markup card's paragraph) -> returned unchanged
     *   - the template has no inheritable properties, or there is none   -> returned unchanged
     *   - otherwise                                                        -> the template's properties become the first child
     */
    static withParagraphPropertiesFrom(request: InheritParagraphPropertiesRequest): string {
        if (request.list === "none") return request.paragraphXml;
        const paragraphDocument = XmlHost.domParser().parseFromString(request.paragraphXml, "application/xml");
        const paragraph = paragraphDocument.documentElement;
        const paragraphHasOwnProperties = Array.from(paragraph.childNodes).some((node) => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === "pPr");
        if (paragraphHasOwnProperties) return request.paragraphXml;
        const templateDocument = XmlHost.domParser().parseFromString(`<fragment ${FRAGMENT_NAMESPACE_DECLARATIONS.join(" ")}>${request.templateParagraphXml}</fragment>`, "application/xml");
        const templateParagraph = Array.from(templateDocument.documentElement.childNodes).find((node): node is Element => node.nodeType === 1);
        const paragraphPropertiesXml = templateParagraph === undefined ? "" : ParagraphXml.inheritableParagraphProperties(templateParagraph);
        if (paragraphPropertiesXml === "") return request.paragraphXml;
        const paragraphPropertiesDocument = XmlHost.domParser().parseFromString(paragraphPropertiesXml, "application/xml");
        paragraph.insertBefore(paragraphDocument.importNode(paragraphPropertiesDocument.documentElement, true), paragraph.firstChild);
        return XmlHost.xmlSerializer().serializeToString(paragraph);
    }

    /**
     * `<w:p>` with the template's paragraph properties and one run of `text`.
     * The template's paragraph-mark run properties (`w:pPr/w:rPr`) are left
     * out because they may carry the neighbour's own revision marks, and the
     * `w14:paraId`/`w14:textId` identity is left out because Word mints a new
     * one; copying it would make two paragraphs claim one identity.
     */
    static plainFromText(request: PlainParagraphFromTextRequest): string {
        const templateDocument = XmlHost.domParser().parseFromString(`<fragment ${FRAGMENT_NAMESPACE_DECLARATIONS.join(" ")}>${request.templateParagraphXml}</fragment>`, "application/xml");
        const templateParagraph = Array.from(templateDocument.documentElement.childNodes).find((node): node is Element => node.nodeType === 1);
        /**
         * CASES
         *   - a template paragraph was given (a stored-file insertion) -> copy its properties
         *   - none was (a text card's insertion, template "")          -> no properties; Word applies the default style
         */
        const paragraphPropertiesXml = templateParagraph === undefined ? "" : ParagraphXml.inheritableParagraphProperties(templateParagraph);
        return `<w:p ${FRAGMENT_NAMESPACE_DECLARATIONS.join(" ")}>${paragraphPropertiesXml}<w:r>${ParagraphXml.runContentOf(request.text)}</w:r></w:p>`;
    }

    /**
     * The children of the one run that holds a new paragraph's text: its words
     * as `w:t`, each tab as `w:tab` and each line break as `w:br`, the way Word
     * writes them and the way the in-paragraph splicer writes inserted text
     * (`ParagraphSplicer`). A tab or line break left inside `w:t` is not a tab
     * or a break to Word.
     *
     * EXAMPLE
     *   text                          = "Name:\tAcme\nTitle:\tCEO"
     *   split(/(\t|\r\n|\r|\n)/)      -> ["Name:", "\t", "Acme", "\n", "Title:", "\t", "CEO"]
     *   run content                   -> <w:t>Name:</w:t><w:tab/><w:t>Acme</w:t><w:br/><w:t>Title:</w:t><w:tab/><w:t>CEO</w:t>
     *
     * CASES, per piece of the split
     *   - "\t"                 -> `<w:tab/>`
     *   - "\r\n", "\r" or "\n" -> `<w:br/>`: each is one line ending, and a "\r" left inside
     *                             `w:t` would be read back by the XML parser as a second
     *                             line break
     *   - "" (between two separators, or at either end) -> nothing, so no empty `w:t` is written
     *   - any other            -> `<w:t xml:space="preserve">` with `&`, `<` and `>` escaped
     */
    private static runContentOf(text: string): string {
        return text
            .split(/(\t|\r\n|\r|\n)/)
            .map((piece) => {
                if (piece === "\t") return "<w:tab/>";
                if (piece === "\r\n" || piece === "\r" || piece === "\n") return "<w:br/>";
                if (piece === "") return "";
                const escapedPiece = piece.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                return `<w:t xml:space="preserve">${escapedPiece}</w:t>`;
            })
            .join("");
    }

    /**
     * The template's `w:pPr` serialized, minus the children a neighbour must
     * not hand on: its paragraph-mark run properties (`w:rPr`, which may carry
     * the neighbour's own revision marks), a section break (`w:sectPr`, which
     * would split the document again) and a tracked property change
     * (`w:pPrChange`, another author's pending formatting revision).
     *
     * WHEN the template has no `w:pPr`
     *   CONSEQUENCE
     *     the new paragraph gets none either, and takes the document default style.
     */
    private static inheritableParagraphProperties(templateParagraph: Element): string {
        const NOT_INHERITED_CHILDREN = ["rPr", "sectPr", "pPrChange"];
        const paragraphProperties = Array.from(templateParagraph.childNodes).find((node): node is Element => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === "pPr");
        // A paragraph with no `w:pPr` has nothing to copy; the new one takes the document default.
        if (paragraphProperties === undefined) return "";
        const paragraphPropertiesClone = paragraphProperties.cloneNode(true) as Element;
        for (const childLocalName of NOT_INHERITED_CHILDREN) {
            for (const notInheritedChild of Array.from(paragraphPropertiesClone.getElementsByTagNameNS(WORDPROCESSINGML_NS, childLocalName))) {
                notInheritedChild.parentNode?.removeChild(notInheritedChild);
            }
        }
        paragraphPropertiesClone.removeAttributeNS(WORDML_2010_NS, "paraId");
        paragraphPropertiesClone.removeAttributeNS(WORDML_2010_NS, "textId");
        return XmlHost.xmlSerializer().serializeToString(paragraphPropertiesClone);
    }
}
